import { useForm } from 'react-hook-form';
import React, { ReactElement, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerSchema, RegisterFormData } from '../../utils/validations';
import { useAuth } from '../../contexts/AuthContext';
import { BASE_URL } from '../../config/api';
import {
  AlertCircle,
  Loader2,
  Info,
  Building2,
  CheckCircle,
  User,
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}
interface AssociadoData {
  data_nascimento: string;
  nome: string;
  email: string;
  empresa_nome?: string;
  empresa_codigo?: string;
  celular: string;
  autorizado: boolean;
  message?: string;
  genero?: string;
  estado?: string;
  isAppleTest?: boolean;
}

const APPLE_TEST_CPFS = ['99988877766', '11122233344', '55566677788'];

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const formatCPF = (value: string) => {
  const cleaned = value.replace(/\D/g, '');
  const part1 = cleaned.substring(0, 3);
  const part2 = cleaned.substring(3, 6);
  const part3 = cleaned.substring(6, 9);
  const part4 = cleaned.substring(9, 11);

  if (cleaned.length <= 3) return part1;
  if (cleaned.length <= 6) return `${part1}.${part2}`;
  if (cleaned.length <= 9) return `${part1}.${part2}.${part3}`;
  return `${part1}.${part2}.${part3}-${part4}`;
};

export function RegisterForm({
  onSuccess,
  onSwitchToLogin,
}: RegisterFormProps): ReactElement {
  const { signUp } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openTermos, setOpenTermos] = useState(false);
  const [openPrivacidade, setOpenPrivacidade] = useState(false);

  //Estado para mostrar senha
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  //Estados para validação do CPF
  const [cpfValidationState, setCpfValidationState] = useState<{
    loading: boolean;
    validated: boolean;
    error: string;
    data: AssociadoData | null;
  }>({
    loading: false,
    validated: false,
    error: '',
    data: null,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      termos: false,
      ofertas: false,
      sms: false,
      origem: '',
      grupo: '',
      empresa: '',
    },
  });

  const cpfValue = watch('cpf') || '';
  const senha = watch('senha') || '';

  useEffect(() => {
    const cpfLimpo = cpfValue.replace(/\D/g, '');

    if (cpfLimpo.length < 11) {
      setCpfValidationState({
        loading: false,
        validated: false,
        error: '',
        data: null,
      });
      return;
    }

    if (cpfLimpo.length === 11) {
      validarAssociado(cpfLimpo);
    }
  }, [cpfValue]);

  const validarAssociado = async (cpf: string) => {
    setCpfValidationState(prev => ({ ...prev, loading: true, error: '' }));
    try {
      const response = await axios.get(
        `${BASE_URL}/club/validarAssociado?cpf=${cpf}`,
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000,
        },
      );

      const associadoData: AssociadoData = response.data;

      if (associadoData.autorizado) {
        setValue('nome', associadoData.nome, { shouldValidate: true });
        setValue('genero', associadoData.genero, { shouldValidate: true });
        setValue('dataNascimento', associadoData.data_nascimento, {
          shouldValidate: true,
        });
        setValue('estado', associadoData.estado, { shouldValidate: true });
        setValue('email', associadoData.email, { shouldValidate: true });
        setValue('celular', associadoData.celular, { shouldValidate: true });
        setValue('confirmarEmail', associadoData.email, {
          shouldValidate: true,
        });

        if (associadoData.empresa_nome) {
          setValue('empresa', associadoData.empresa_nome, {
            shouldValidate: true,
          });
        }

        if (associadoData.isAppleTest) {
          clearErrors('cpf');
        }

        setValue('senha', 'AppleTest@2024', { shouldValidate: true });
        setValue('confirmarSenha', 'AppleTest@2024', { shouldValidate: true });
        clearErrors('cpf');
        clearErrors('senha');
        clearErrors('confirmarSenha');

        clearErrors('cpf');

        setCpfValidationState({
          loading: false,
          validated: true,
          error: '',
          data: associadoData,
        });
      } else {
        const errorMsg =
          associadoData.message ||
          'CPF não autorizado para cadastro no Clube de Benefícios';

        setCpfValidationState({
          loading: false,
          validated: false,
          error: errorMsg,
          data: null,
        });

        setError('cpf', {
          type: 'manual',
          message: errorMsg,
        });
      }
    } catch (error: any) {
      console.error('Erro na validação do CPF:', error.response);

      let errorMsg = 'Erro ao validar CPF. Tente novamente.';

      if (error.response?.status === 404) {
        errorMsg = 'CPF não encontrado ou não autorizado para cadastro';
      } else if (error.response?.status === 400) {
        errorMsg = error.response.data.mensagem || 'CPF inválido';
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = 'Timeout na validação. Verifique sua conexão.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }

      setCpfValidationState({
        loading: false,
        validated: false,
        error: errorMsg,
        data: null,
      });

      setError('cpf', {
        type: 'manual',
        message: errorMsg,
      });
    }
  };

  const hasUppercase = /[A-Z]/.test(senha);
  const hasLowercase = /[a-z]/.test(senha);
  const hasNumber = /[0-9]/.test(senha);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(senha);
  const hasQuantity = senha.length > 5;

  const onSubmit = async (data: RegisterFormData) => {
    if (!cpfValidationState.validated) {
      setErrorMessage('CPF deve ser validado antes do cadastro');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const dadosParaBackend = {
        nome: data.nome,
        cpf: data.cpf?.replace(/\D/g, ''),
        genero: data.genero,
        celular: data.celular?.replace(/\D/g, ''),
        data_nascimento: data.dataNascimento,
        estado: data.estado,
        aceite_termos: data.termos ? 1 : 0,
        aceite_ofertas: data.ofertas ? 1 : 0,
        aceite_sms: data.sms ? 1 : 0,
        empresa: data.empresa || cpfValidationState.data?.empresa_nome || '',
        empresa_codigo: cpfValidationState.data?.empresa_codigo || '',
        observacoes: `Cadastro via Clube de Benefícios - Associado validado`,
      };

      await signUp(data.email, data.senha, dadosParaBackend);

      onSuccess?.();
      navigate('/ClubeBeneficios', { replace: true }); // Redireciona para a página de login
    } catch (error: any) {
      console.error('Erro no cadastro:', error);
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          'Erro ao realizar cadastro. Tente novamente.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-1xl mx-auto bg-white rounded-2xl shadow-lg p-8 mt-5">
      {/* Banner informativo */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-blue-800 mb-1">
              Clube de Benefícios - Cadastro de Associados
            </h3>
            <p className="text-sm text-blue-700">
              Digite seu CPF para verificar se você está habilitado a se
              cadastrar no Clube de Benefícios.
            </p>
          </div>
        </div>
      </div>

      {cpfValidationState.validated && cpfValidationState.data?.isAppleTest && (
        <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-purple-800 mb-1">
                Modo Teste Apple Sandbox
              </h3>
              <p className="text-sm text-purple-700">
                CPF de teste detectado. Dados preenchidos automaticamente para
                teste de integração.
              </p>
            </div>
          </div>
        </div>
      )}

      {/*Status da validação do CPF */}
      {cpfValidationState.loading && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-yellow-600 animate-spin" />
            <p className="text-sm text-yellow-800">
              Validando CPF... Aguarde um momento.
            </p>
          </div>
        </div>
      )}

      {/* {cpfValidationState.validated && cpfValidationState.data && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-green-800 font-medium">
                CPF habilitado! Dados preenchidos automaticamente:
              </p>
              <div className="mt-2 text-xs text-green-700">
                <div>
                  <strong>Nome:</strong> {cpfValidationState.data.nome}
                </div>
                <div>
                  <strong>Email:</strong> {cpfValidationState.data.email}
                </div>
                {cpfValidationState.data.empresa_nome && (
                  <div>
                    <strong>Empresa:</strong>{' '}
                    {cpfValidationState.data.empresa_nome}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )} */}

      {cpfValidationState.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{cpfValidationState.error}</p>
          </div>
        </div>
      )}

      <div className="mb-6 mt-5">
        <div className="text-sm text-gray-500 mb-2">
          <span className="text-gray-400">Início</span>
          <span className="mx-2">{'>'}</span>
          <span className="text-gray-700">Cadastro</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2 mt-5">
          Faça seu cadastro
        </h1>
        <p className="text-sm text-gray-600">
          Campos com <span className="text-red-500">*</span> são obrigatórios
        </p>
      </div>

      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/*Campo CPF com validação automática */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CPF <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                {...register('cpf')}
                type="text"
                placeholder="000.000.000-00"
                maxLength={14}
                onChange={e => {
                  const formatted = formatCPF(e.target.value);
                  setValue('cpf', formatted, { shouldValidate: true });
                }}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:border-transparent transition-all outline-none ${
                  cpfValidationState.loading
                    ? 'border-yellow-300 focus:ring-yellow-500'
                    : cpfValidationState.validated
                    ? 'border-green-300 focus:ring-green-500'
                    : errors.cpf
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
              />

              {/*Ícone de status */}
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {cpfValidationState.loading && (
                  <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />
                )}
                {cpfValidationState.validated && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
                {cpfValidationState.error && (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>

            {errors.cpf && (
              <p className="mt-1 text-sm text-red-600">{errors.cpf.message}</p>
            )}
          </div>

          {/*Campo Nome (preenchido automaticamente) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                {...register('nome')}
                type="text"
                placeholder="Digite seu nome completo"
                readOnly={cpfValidationState.validated} //Somente leitura se validado
                className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${
                  cpfValidationState.validated ? 'bg-gray-50' : ''
                }`}
              />
              {cpfValidationState.validated && (
                <User className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-500" />
              )}
            </div>
            {errors.nome && (
              <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
            )}
          </div>

          {/*Campo Email (preenchido automaticamente) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              E-mail <span className="text-red-500">*</span>
            </label>
            <input
              {...register('email')}
              type="email"
              placeholder="seu@email.com"
              readOnly={cpfValidationState.validated} //Somente leitura se validado
              className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none ${
                cpfValidationState.validated ? 'bg-gray-50' : ''
              }`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/*Campo Confirmar Email (preenchido automaticamente) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar e-mail <span className="text-red-500">*</span>
            </label>
            <input
              {...register('confirmarEmail')}
              type="email"
              placeholder="Confirme seu e-mail"
              readOnly={false}
              className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none`}
            />
            {errors.confirmarEmail && (
              <p className="mt-1 text-sm text-red-600">
                {errors.confirmarEmail.message}
              </p>
            )}
          </div>

          {/* Resto dos campos mantidos iguais... */}
          {/* Campo de senha com validação visual */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                {...register('senha')}
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>

            <p className="mt-1 text-xs text-gray-500">
              A senha deve conter pelo menos 6 caracteres, letras maiúsculas,
              minúsculas, números e caracteres especiais.
            </p>

            <div className="flex flex-col mt-2 text-xs text-gray-700 bg-[beige] w-[30%]">
              <label className="flex items-center gap-2">
                <span
                  className={hasUppercase ? 'text-green-600' : 'text-red-500'}
                >
                  Letra maiúscula {!hasUppercase ? '*' : ''}
                </span>
              </label>

              <label className="flex items-center gap-2">
                <span
                  className={hasLowercase ? 'text-green-600' : 'text-red-500'}
                >
                  Letra minúscula {!hasLowercase ? '*' : ''}
                </span>
              </label>

              <label className="flex items-center gap-2">
                <span className={hasNumber ? 'text-green-600' : 'text-red-500'}>
                  Números {!hasNumber ? '*' : ''}
                </span>
              </label>

              <label className="flex items-center gap-2">
                <span
                  className={hasSpecialChar ? 'text-green-600' : 'text-red-500'}
                >
                  Caractere especial {!hasSpecialChar ? '*' : ''}
                </span>
              </label>
              <label className="flex items-center gap-2">
                <span
                  className={hasQuantity ? 'text-green-600' : 'text-red-500'}
                >
                  Mínimo 6 caracteres {!hasQuantity ? '*' : ''}
                </span>
              </label>
            </div>

            {errors.senha && (
              <p className="mt-1 text-sm text-red-600">
                {errors.senha.message}
              </p>
            )}
          </div>

          <div className="">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar senha<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                {...register('confirmarSenha')}
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirme sua senha"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showConfirmPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>

            {errors.confirmarSenha && (
              <p className="mt-1 text-sm text-red-600">
                {errors.confirmarSenha.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gênero<span className="text-red-500">*</span>
            </label>
            <select
              {...register('genero')}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-white"
            >
              <option value="">Selecione...</option>
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
              <option value="O">Outro</option>
            </select>
            {errors.genero && (
              <p className="mt-1 text-sm text-red-600">
                {errors.genero.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Celular<span className="text-red-500">*</span>
            </label>
            {/* <input
              {...register('celular')}
              type="text"
              placeholder="00 00000-0000"
              maxLength={15}
              value={watch('celular') || ''}
               onChange={e => {
                let value = e.target.value.replace(/\D/g, '');

                if (value.length > 11) value = value.slice(0, 11);

                if (value.length > 6) {
                  value = value.replace(
                    /^(\d{2})(\d{5})(\d{0,4})/,
                    '($1) $2-$3',
                  );
                } else if (value.length > 2) {
                  value = value.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
                } else if (value.length > 0) {
                  value = value.replace(/^(\d{0,2})/, '($1');
                }

                setValue('celular', value);
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
            /> */}
            <input
              {...register('celular')}
              type="text"
              placeholder="00 000000000"
              maxLength={11}
              value={watch('celular') || ''}
              onChange={e => {
                setValue('celular', e.target.value.replace(/\D/g, ''));
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {errors.celular && (
              <p className="mt-1 text-sm text-red-600">
                {errors.celular.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Nascimento<span className="text-red-500">*</span>
            </label>
            <input
              {...register('dataNascimento')}
              type="date"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
            />
            {errors.dataNascimento && (
              <p className="mt-1 text-sm text-red-600">
                {errors.dataNascimento.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estado <span className="text-red-500">*</span>
            </label>
            <select
              {...register('estado')}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none bg-white"
            >
              <option value="">Escolha um estado</option>
              <option value="AC">Acre</option>
              <option value="AL">Alagoas</option>
              <option value="AP">Amapá</option>
              <option value="AM">Amazonas</option>
              <option value="BA">Bahia</option>
              <option value="CE">Ceará</option>
              <option value="DF">Distrito Federal</option>
              <option value="ES">Espírito Santo</option>
              <option value="GO">Goiás</option>
              <option value="MA">Maranhão</option>
              <option value="MT">Mato Grosso</option>
              <option value="MS">Mato Grosso do Sul</option>
              <option value="MG">Minas Gerais</option>
              <option value="PA">Pará</option>
              <option value="PB">Paraíba</option>
              <option value="PR">Paraná</option>
              <option value="PE">Pernambuco</option>
              <option value="PI">Piauí</option>
              <option value="RJ">Rio de Janeiro</option>
              <option value="RN">Rio Grande do Norte</option>
              <option value="RS">Rio Grande do Sul</option>
              <option value="RO">Rondônia</option>
              <option value="RR">Roraima</option>
              <option value="SC">Santa Catarina</option>
              <option value="SP">São Paulo</option>
              <option value="SE">Sergipe</option>
              <option value="TO">Tocantins</option>
            </select>
            {errors.estado && (
              <p className="mt-1 text-sm text-red-600">
                {errors.estado.message}
              </p>
            )}
          </div>
        </div>

        {/* Termos e Condições */}
        <div className="space-y-4 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Termos e Condições
          </h3>

          <label className="flex items-start gap-3">
            <input
              {...register('termos')}
              type="checkbox"
              className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            {/* <span className="text-sm text-gray-700">
              Aceito os{' '}
              <a href="#" className="text-blue-600 hover:underline">
                Termos de Uso
              </a>{' '}
              e{' '}
              <a href="#" className="text-blue-600 hover:underline">
                Política de Privacidade
              </a>
              <span className="text-red-500">*</span>
            </span> */}
            <span className="text-sm text-gray-700">
              Aceito os{' '}
              <button
                type="button"
                className="text-blue-600 hover:underline bg-transparent border-none p-0 m-0"
                onClick={() => setOpenTermos(true)}
              >
                Termos de Uso
              </button>{' '}
              e{' '}
              <button
                type="button"
                className="text-blue-600 hover:underline bg-transparent border-none p-0 m-0"
                onClick={() => setOpenPrivacidade(true)}
              >
                Política de Privacidade
              </button>
              <span className="text-red-500">*</span>
            </span>
          </label>

          {errors.termos && (
            <p className="text-sm text-red-600">{errors.termos.message}</p>
          )}

          {/* Modal Termos de Uso */}
          {openTermos && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
                <h2 className="text-lg font-bold mb-4">Termos de Uso</h2>
                <p className="mb-4">
                  {/* Conteúdo dos Termos */}
                  I. FINALIDADE Este termo regulamenta a utilização do Clube Ben
                  e estabelece as competências e campo de atuação referente ao
                  mesmo. II. DEFINIÇÕES E ABREVIATURAS Usuário: Clientes,
                  empregados e estagiários que trabalhem na empresa ou em outras
                  empresas vinculadas com contrato ativo com a Proxy Media
                  Informação e Comunicação Ltda. Clube de Vantagens Clube Ben:
                  Programa de benefícios e vantagens criado e administrado pela
                  empresa Proxy Media Informação e Comunicação Ltda de uso
                  exclusivo do usuário. Rede Conveniada: Conjunto de
                  estabelecimentos comerciais, pessoas jurídicas cadastradas
                  junto a Proxy Media Informação e Comunicação Ltda que
                  viabilizam o clube de vantagens Clube Ben. Esses
                  estabelecimentos podem ser lojas físicas ou on-line.
                  Transação: Condições comerciais exclusivas oferecidas pelos
                  estabelecimentos da rede conveniada e, de sua
                  responsabilidade, visando o relacionamento e fidelização do
                  usuário. O usuário que desejar conhecer os descontos ou
                  condições exclusivas oferecidas por um dos parceiros, deverá
                  acessar o hotsite do clube de vantagens Clube Ben, disponível
                  no link divulgado. Para o caso das lojas físicas, para obter o
                  desconto, o usuário deverá apresentar no parceiro selecionado;
                  o cupom impresso (gerado pela plataforma) ou o crachá da
                  empresa, e um documento identificado com foto. III. PRINCÍPIOS
                  BÁSICOS Considerações Gerais 1.1 O Termo de Uso deste site
                  rege os serviços oferecidos pelo clube de vantagens Clube Ben,
                  cuja utilização pressupõe a mais alta compreensão e vinculação
                  de todas as regras aqui estabelecidas. Objeto 2.1 Produtos
                  e/ou serviços serão anunciados no site, em nome do parceiro,
                  juntamente com as respectivas regras, destaques e informações
                  necessárias ao usuário quanto aos requisitos para aquisição
                  dos mesmos, por período determinado ou não, a variar conforme
                  o caso. Responsabilidade 3.1 O clube de vantagens Clube Ben
                  não é o proprietário dos produtos e/ou serviços anunciados
                  pelos parceiros, não tem sua posse e não realiza as promoções
                  em seu nome, tampouco intervém na entrega e/ou uso dos
                  produtos e/ou serviços. 3.2 O clube de vantagens Clube Ben é
                  responsável pela divulgação das promoções e descontos no site
                  e outros meios de comunicação interna, não fazendo parte da
                  cadeia de consumo ou fornecimento do produto e/ou serviço
                  adquirido. 3.3 Muito embora o clube de vantagens Clube Ben
                  adote uma política de segurança e pretenda tomar todas as
                  providências, dentro do razoável, para prevenir a introdução
                  de vírus ou outros materiais destrutivos no site dos
                  programas, não garante o acesso ininterrupto ou livre de erros
                  em seu site na web, não assumindo qualquer responsabilidade
                  por danos causados em virtude do acesso ao site ou por
                  impossibilidade de acessá-lo. Qualquer tentativa de violar os
                  sistemas e/ou banco de dados do clube de vantagens Clube Ben
                  importará, além das sanções aqui previstas por violação deste
                  Regulamento, em ações judiciais cabíveis e indenizações por
                  eventuais danos causados. 3.4 Em nenhuma oportunidade,
                  qualquer das empresas com as quais o clube de vantagens Clube
                  Ben mantenha associação corporativa incluindo a participação
                  sobre os direitos de marca, marketing, desenvolvimento
                  conjunto, relacionamento comercial ou de fornecimento, será
                  responsável perante o participante por falha de desempenho do
                  clube de vantagens. Em nenhum caso, o clube de vantagens Clube
                  Ben e/ou seus controladores, seus diretores e agentes, serão
                  responsáveis por quaisquer danos, reclamações ou perdas
                  sofridas, incluindo, mas sem limitações, aqueles relacionados:
                  ao programa/clube; a qualquer falha, atraso ou decisão na
                  administração do programa/clube de vantagens; a utilização ou
                  impossibilidade de utilizar o site do clube de vantagens
                  “Clube Ben” na web; a compra ou uso de qualquer produto ou
                  serviço oferecido pelos parceiros e/ou fornecedores, mesmo que
                  o clube de vantagens “Clube Ben” e/ou seus representantes
                  sejam advertidos da possível ocorrência de tais danos ao
                  adquirir um produto e/ou serviço, o usuário declara conhecer
                  as condições de uso, pagamento, recebimento e prazo de
                  validade destes. Penalidades 4.1 O usuário que infringir a
                  legislação aplicável ou os compromissos aqui assumidos estará
                  sujeito a sanções previstas neste Termo de Uso, não se
                  escusando de responder a eventuais ações cíveis e penais pelas
                  suas condutas. Propriedade intelectual 5.1 O uso comercial da
                  expressão Clube Ben como marca, nome empresarial ou nome de
                  domínio, bem como os conteúdos das telas relativas aos
                  serviços do clube de vantagens, assim como os programas,
                  bancos de dados, redes e arquivos, são propriedade do clube de
                  vantagens Clube Ben e estão protegidos pelas leis e tratados
                  internacionais de direito autoral, marcas, patentes, modelos e
                  desenhos industriais. O uso indevido e a reprodução total ou
                  parcial dos referidos conteúdos são proibidos, salvo
                  autorização expressa do clube de vantagens Clube Ben.
                  Cancelamento e Alteração do Programa 6.1 O clube de vantagens
                  Clube Ben reserva-se o direito de cancelar o programa a
                  qualquer momento, independente de notificação prévia. Além
                  disso, reserva-se o direito de alterar o presente acordo a
                  qualquer momento, no todo ou em parte, independente de
                  notificação prévia, sendo que tais modificações serão
                  presumidas como aceitação tácita das novas regras pelos
                  participantes do Programa/Clube.
                </p>
                <button
                  onClick={() => setOpenTermos(false)}
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Modal Política de Privacidade */}
          {openPrivacidade && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg max-w-lg w-full relative max-h-[80vh] overflow-y-auto">
                <h2 className="text-lg font-bold mb-4">
                  Política de Privacidade
                </h2>
                <p className="mb-4">
                  {/* Conteúdo da Política */}
                  Política de Privacidade Clube Ben – Proxy Media Leia essa
                  Política de Privacidade para entender nossas políticas sobre
                  seus Dados Pessoais e a forma como os trataremos. A Proxy
                  Media se preocupa com a sua privacidade e a respeita. Esta
                  política de privacidade explica quais tipos de informações
                  coletamos por meio de nossos sites, como essas informações
                  serão utilizadas, e com quem essas informações serão
                  compartilhadas, visando a total transparência entre a Proxy
                  Media e você, usuário, em consonância com a Lei nº 13.709/18.
                  1 - QUAIS SÃO AS INFORMAÇÕES QUE COLETAMOS? a. A Proxy Media
                  coleta, registra e armazena apenas as informações fornecidas e
                  consentidas por você em nossos sites. A autenticidade dos
                  dados fornecidos é de extrema importância e depende somente de
                  você, portanto, verifique seus dados antes de nos enviar.
                  Assim, você é o responsável exclusivo pelas declarações
                  prestadas em nossos sites e que vierem a causar prejuízos a si
                  mesmo, à Proxy Media e/ou a terceiros, podendo corrigi-los ou
                  alterá-los a qualquer tempo. b. O usuário que não desejar mais
                  receber mensagens através dos domínios remetentes de nossos
                  sites, nos termos do item 1.a, deverá enviar um email para o
                  endereço contatoLGPD@clubeben.com.br, manifestando seu
                  interesse e terá automaticamente seu cadastro eliminado do
                  nosso banco de dados. c. Caso suspeite de qualquer uso
                  indevido dos seus dados, não deixe de entrar em contato
                  conosco, por meio do campo de contato do item 1.b. d. Caso
                  deseje acessar seus dados, alterá-los e receber informações
                  sobre eventuais compartilhamentos, entrar em contato conosco,
                  por meio do campo de contato do item 1.b. 2 - PARA QUAL
                  FINALIDADE OS DADOS SÃO UTILIZADOS? O usuário ao acessar
                  nossos sites, permite que a Proxy Media no papel de gestora,
                  tenha a liberdade para utilizá-los para o envio de mensagens
                  de cunho publicitário, novidades, ofertas e promoções da
                  promotora, através dos domínios remetentes de nossos sites,
                  bem como o envio de ofertas de produtos e/ou serviços dos
                  nossos parceiros, sendo o usuário, aceitando os termos da
                  política de privacidade e condições dos nossos parceiros,
                  passando você a se submeter à política de privacidade da
                  aplicação acessada. 3 - SEUS DIREITOS A Proxy Media
                  disponibiliza a você, mediante solicitação, a possibilidade de
                  consulta, correção, atualização, portabilidade, exclusão, além
                  da possibilidade de se obter informações sobre
                  compartilhamento e anonimização de suas informações pessoais,
                  mantidas no nosso Banco de Dados. Caso você deseje exercer
                  seus direitos em relação às suas informações pessoais ou caso
                  tenha alguma dúvida sobre como usamos suas informações
                  pessoais, entre em contato conosco através dos seguintes
                  canais: E-mail: contatoLGPD@proxymedia.com.br Telefone: (11)
                  2825-0790 Endereço: Rua Tito, 678 – cj 104, 105, 106, Bairro:
                  Lapa Cidade/UF: São Paulo – SP CEP: 05051-000 4 – COOKIES A
                  Proxy Media assegura que não usufrui de nenhuma informação
                  pessoal via cookies em nossos sites. Toda e qualquer
                  informação armazenada pelo banco de dados da Proxy Media é
                  fornecida pelo usuário, com seu expresso consentimento. 5- LOG
                  DE ACESSO A Proxy Media realiza a coleta e guarda dos dados
                  sobre o seu acesso em nossos sites, conforme determinação
                  legal, e mantém seus dados arquivados até que estes atinjam os
                  fins a que se destinam, podendo o usuário solicitar tais
                  informações a qualquer momento. 6 - COMO OS DADOS
                  COMPARTILHADOS SÃO PROTEGIDOS? A Política de Privacidade da
                  Proxy Media, visa garantir que quaisquer informações e dados
                  dos usuários não serão fornecidos, publicados ou
                  comercializados em quaisquer circunstância, assim, tomamos as
                  medidas de segurança e de proteção de dados necessárias para a
                  evitar riscos de perda, mau uso, alteração, acessos não
                  autorizados ou subtração dos dados por você compartilhados.
                  Nossas medidas de segurança estão sendo melhoradas
                  constantemente, de acordo com o desenvolvimento tecnológico.
                  Ao aceitar a política de privacidade o usuário se submete
                  também à política de privacidade da empresa promotora do
                  clube. 7 - AUTORIDADE PÚBLICA A Proxy Media somente
                  disponibilizará seus dados às Autoridades Públicas mediante
                  ordem judicial expressa, e para tanto for feito nos moldes da
                  legislação e que iremos fornecer suas informações para a
                  autoridade pública competente. Não efetuamos essa entrega de
                  forma excessiva, ou quando não estiver em conformidade com as
                  determinações legais. 8 - MENORES DE 12 ANOS A Proxy Media não
                  armazena, coleta ou trata dados de crianças menores de 13
                  (treze) anos de idade. Assim, se você é menor de 13 (treze)
                  anos, não poderá se cadastrar em nossos sites, enviando-nos
                  informações pessoais. Se necessário, deverá solicitar ao seu
                  responsável legal que realize o cadastro em nossos sites, com
                  consentimento explicito daquela pessoa, e represente-o junto a
                  este site. 9 - OUTRAS APLICAÇÕES Quando você acessar
                  plataformas e aplicativos de terceiros, a partir da navegação
                  por meio dos nossos sites (ex. links do facebook, linkedin,
                  youtube e instagram), você usuário passa a se submeter à
                  política de privacidade da aplicação acessada, de modo que a
                  presente política não se aplica aos dados pessoais coletados
                  ou por você compartilhados nessas aplicações. A Proxy Media
                  garante a privacidade dos dados pessoais do usuário coletados
                  dentro da sua plataforma. 10 - ALTERAÇÕES A ESTA POLÍTICA DE
                  PRIVACIDADE a) - A presente política de privacidade está
                  sujeita a alterações regulares para garantir que esteja
                  atualizada com o uso de suas informações pessoais e em
                  conformidade com as leis de proteção de dados aplicáveis. Por
                  favor, verifique com frequência esta Política de Privacidade
                  para se informar sobre possíveis alterações que possam vir a
                  ser feitas. b) - A Proxy Media reserva-se o direito de alterar
                  esta política de privacidade, de tempos em tempos, seja pela
                  utilização de novas tecnologias ou sempre que a Proxy Media
                  entender necessário, de acordo com sua política de Compliance.
                  A política de privacidade atualizada será publicada em nosso
                  site, bem como todos os usuários serão informados de eventuais
                  alterações. 11 - LEI GERAL DE PROTEÇÃO DE DADOS A Proxy Media
                  é uma das primeiras Empresas de Marketing Digital a buscar
                  adequação em conformidade com a Lei Geral de Proteção de
                  Dados, que entrará em vigor em 01/08/2021. A Proxy Media
                  garante que seus fornecedores, clientes e parceiros estejam
                  também alinhados à Lei Geral de Proteção de Dados, trabalhando
                  sempre com informações públicas ou com consentimento do
                  usuário. A Proxy Media está reestruturando seus processos
                  internos de armazenamento e controle de dados de seus
                  usuários, adequando-se às exigências determinadas pela Lei
                  Geral de Proteção de dados, que entrará em vigor em
                  01/08/2021. Para efeito de entendimento, a Proxy Media se
                  enquadra ás figuras de “Controlador”, possuidor dos dados e
                  tomador de decisões a respeito do tratamento dos dados
                  pessoais a depender da atividade que estivemos desenvolvendo
                  para os nossos clientes.
                </p>
                <button
                  onClick={() => setOpenPrivacidade(false)}
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          <label className="flex items-start gap-3">
            <input
              {...register('ofertas')}
              type="checkbox"
              className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Desejo receber ofertas e promoções por e-mail
            </span>
          </label>

          <label className="flex items-start gap-3">
            <input
              {...register('sms')}
              type="checkbox"
              className="w-4 h-4 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              Desejo receber ofertas e promoções por SMS
            </span>
          </label>
        </div>

        {/* Botão de submissão */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            disabled={
              isSubmitting ||
              !cpfValidationState.validated ||
              cpfValidationState.loading
            }
            className={`
              w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md 
              flex items-center justify-center gap-2
              ${
                isSubmitting ||
                !cpfValidationState.validated ||
                cpfValidationState.loading
                  ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-lg'
              }
            `}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Cadastrando...
              </>
            ) : (
              'Cadastrar'
            )}
          </button>
        </div>

        {/* Mensagem informativa sobre validação */}
        {!cpfValidationState.validated && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Digite um CPF válido para continuar com o cadastro
            </p>
          </div>
        )}

        {onSwitchToLogin && (
          <div className="text-center pt-4 border-t">
            <p className="text-sm text-gray-600">
              Já tem uma conta?{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
              >
                Fazer login
              </button>
            </p>
          </div>
        )}
      </form>
    </div>
  );
}

export default RegisterForm;
